// src/lib/api.js
// Production-ready API client for same-origin Hosting rewrite -> Functions (/api/**)

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").trim(); // "" => same-origin

function joinUrl(base, path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base.replace(/\/+$/, "")}${p}` : p;
}

async function parseJsonSafe(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

// Use a unique name to avoid any bundler/scope collisions.
async function apiRequest(
  path,
  { method = "POST", body, headers, timeoutMs = 20000 } = {}
) {
  const url = joinUrl(API_BASE, path);
  const upper = String(method || "POST").toUpperCase();

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const init = {
      method: upper,
      headers: {
        ...(upper !== "GET" ? { "Content-Type": "application/json" } : {}),
        ...(headers || {}),
      },
      // REQUIRED for cookie sessions (same-origin or cross-origin)
      credentials: "include",
      signal: controller.signal,
    };

    if (upper !== "GET" && body !== undefined) {
      init.body = JSON.stringify(body);
    }

    let res;
    try {
      res = await fetch(url, init);
    } catch (e) {
      const err = new Error(
        `Network error calling API: ${url}. ` +
          `If you previously used localhost, ensure REACT_APP_API_BASE_URL is empty in production.`
      );
      err.isNetworkError = true;
      err.cause = e;
      throw err;
    }

    const data = await parseJsonSafe(res);

    if (!res.ok) {
      const err = new Error(data?.message || data?.error || `API error ${res.status}`);
      err.status = res.status;
      err.url = url;
      err.response = data;
      throw err;
    }

    return data;
  } finally {
    clearTimeout(t);
  }
}

export const Api = {
  // Auth (password + OTP MFA)
  register: (payload) => apiRequest("/api/register", { method: "POST", body: payload }),
  loginPassword: (payload) => apiRequest("/api/login", { method: "POST", body: payload }),
  verifyMfa: (payload) => apiRequest("/api/verify-mfa", { method: "POST", body: payload }),

  // Passwordless (OTP)
  sendCode: (email) => apiRequest("/api/send-code", { method: "POST", body: { email } }),
  verifyCode: (email, code) =>
    apiRequest("/api/verify-code", { method: "POST", body: { email, code } }),

  // Payload variants (phone or email)
  sendCodePayload: (payload) => apiRequest("/api/send-code", { method: "POST", body: payload }),
  verifyCodePayload: (payload) => apiRequest("/api/verify-code", { method: "POST", body: payload }),

  // Social MFA start
  socialStartMfa: (payload) =>
    apiRequest("/api/social/start-mfa", { method: "POST", body: payload }),

  // Session introspection (cookie-backed)
  me: () => apiRequest("/api/me", { method: "GET" }),

  meOptional: async () => {
    try {
      return await apiRequest("/api/me", { method: "GET" });
    } catch (e) {
      if (e?.status === 401) return { authenticated: false };
      throw e;
    }
  },

  // Upgrade cookie session -> Firebase Auth (custom token)
  sessionFirebaseCustomToken: () =>
    apiRequest("/api/session/firebaseCustomToken", { method: "POST", body: {} }),

  logout: () => apiRequest("/api/logout", { method: "POST" }),
};
